/**
 * 🟥 CUADRO NEGRO DEFINITIVO 2026-06-27 — Wrapper bilingüe sobre sonner.
 *
 * `showSuccess(key)` muestra un toast verde con el mensaje correspondiente
 * al idioma activo. Detecta el idioma fuera del árbol React leyendo:
 *   1. Cookie `damyxia_lang` (cross-device, firmada por backend).
 *   2. localStorage `damyxia-language` (fallback local).
 *   3. `'es'` (default).
 *
 * Usa el `toast` de sonner que ya está montado en `App.js` con
 * `<Toaster richColors closeButton />` — los success toasts salen en
 * VERDE automáticamente.
 *
 * Uso:
 *   import { showSuccess } from '@/utils/showSuccess';
 *   showSuccess('image_sent');
 *   showSuccess('visual_created');
 *
 * Para casos sin clave en el diccionario, podés pasar un objeto literal:
 *   showSuccess({ es: 'Listo!', en: 'Done!' });
 */
import { toast } from 'sonner';
import { successMessages } from './successMessages';

const LANGUAGE_STORAGE_KEY = 'damyxia-language';
const LANGUAGE_COOKIE_NAME = 'damyxia_lang';

const readCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
};

/** Lee el idioma activo SIN depender del Context (uso desde handlers fuera de React). */
export const getCurrentLanguage = () => {
  try {
    const cookieLang = readCookie(LANGUAGE_COOKIE_NAME);
    if (cookieLang === 'es' || cookieLang === 'en') return cookieLang;
    const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(LANGUAGE_STORAGE_KEY) : null;
    if (saved === 'es' || saved === 'en') return saved;
  } catch {
    /* fallback abajo */
  }
  return 'es';
};

/**
 * Muestra un toast de éxito (verde) con el texto bilingüe correspondiente.
 * @param {string | { es: string, en: string }} keyOrLiteral
 * @param {object} [options] opciones passthrough a sonner (duration, action, etc.)
 */
export const showSuccess = (keyOrLiteral, options) => {
  const lang = getCurrentLanguage();
  let msg = '';
  if (typeof keyOrLiteral === 'string') {
    const entry = successMessages[keyOrLiteral];
    if (!entry) {
      // Si la clave no existe, mostrar la clave literal (defensa, no romper).
      msg = keyOrLiteral;
    } else {
      msg = entry[lang] || entry.en || entry.es || keyOrLiteral;
    }
  } else if (keyOrLiteral && typeof keyOrLiteral === 'object') {
    msg = keyOrLiteral[lang] || keyOrLiteral.en || keyOrLiteral.es || '';
  }
  if (!msg) return;
  toast.success(msg, options);
};

// 🟥 Expone showSuccess en window para QA/E2E desde DevTools y Playwright.
// Útil también para soporte: el creador puede inspeccionar el helper
// en producción sin necesidad de buscar la build minificada. No abre
// ningún vector de seguridad — sólo dispara toasts.
if (typeof window !== 'undefined') {
  window.damyxiaShowSuccess = showSuccess;
}

export default showSuccess;
